/**
 * Cleanroom login — development only, never reachable in a production build.
 *
 * Intentionally not translated: this screen is dead-code-eliminated from
 * production bundles and is never shown to a user, so adding it to the `auth`
 * namespace would pollute the locale files with strings nobody can read.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Flex,
  HStack,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  discoverIdentities,
  fetchCleanroomToken,
  storeSession,
} from '../../auth/cleanroomAuth';
import { cardSurface } from '../shared/Card';

const ROLE_COLORS = {
  Owner: 'purple',
  'Co-Owner': 'blue',
  Member: 'green',
  Advisor: 'orange',
  Viewer: 'gray',
};

const CleanroomLogin = () => {
  const [identities, setIdentities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signingIn, setSigningIn] = useState(null);

  useEffect(() => {
    let cancelled = false;
    discoverIdentities()
      .then((found) => {
        if (cancelled) return;
        setIdentities(found);
        if (found.length === 0) {
          setError(
            'No seeded users found. Is the cleanroom running, and has it been ' +
              'bootstrapped?'
          );
        }
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (identity) => {
    setSigningIn(identity.sub);
    setError(null);
    try {
      const token = await fetchCleanroomToken(identity.sub);
      storeSession(token, identity.sub);
      // Full reload: six contexts hold per-user state and there is no query
      // cache to invalidate selectively. `/` picks the start page: Home on
      // the phone, Movements on the desktop.
      window.location.assign('/');
    } catch (e) {
      setError(e.message);
      setSigningIn(null);
    }
  }, []);

  return (
    <Flex minHeight="100vh" align="center" justify="center" bg="redesign.bg.page" p={4}>
      <Box {...cardSurface} p={8} w="100%" maxW="460px">
        <Box textAlign="center" mb={5}>
          <Text fontSize="2xl" fontWeight="bold" letterSpacing="-0.02em" color="redesign.heading">
            Mi Kapital
          </Text>
          <Badge bg="rgba(185, 28, 28, 0.08)" _dark={{ bg: 'rgba(248, 113, 113, 0.14)' }} color="redesign.accent.red" mt={2}>
            Cleanroom — development only
          </Badge>
        </Box>

        <Alert status="warning" bg="rgba(180, 83, 9, 0.12)" _dark={{ bg: 'rgba(250, 204, 21, 0.14)' }} borderRadius="md" mb={5} fontSize="sm">
          <AlertIcon color="redesign.accent.amber" />
          <Box>
            No password is checked. Tokens are signed locally and only accepted
            while the backend runs with <Code fontSize="xs">CLEANROOM_MODE=true</Code>.
          </Box>
        </Alert>

        {error && (
          <Alert status="error" bg="rgba(185, 28, 28, 0.08)" _dark={{ bg: 'rgba(248, 113, 113, 0.14)' }} borderRadius="md" mb={4} fontSize="sm">
            <AlertIcon color="redesign.accent.red" />
            <Box>{error}</Box>
          </Alert>
        )}

        {loading ? (
          <Flex justify="center" py={8}>
            <Spinner />
          </Flex>
        ) : (
          <Stack spacing={2}>
            {identities.map((identity) => (
              <Button
                key={identity.sub}
                onClick={() => signIn(identity)}
                isLoading={signingIn === identity.sub}
                isDisabled={signingIn !== null}
                variant="outline"
                justifyContent="space-between"
                height="auto"
                py={3}
                px={4}
                width="100%"
              >
                <HStack spacing={3}>
                  <Text fontWeight="600">{identity.username}</Text>
                  {identity.dataset === 'demo' && (
                    <Badge bg="redesign.bg.card" color="redesign.text.muted" fontSize="0.65em">
                      demo
                    </Badge>
                  )}
                </HStack>
                <Badge colorScheme={ROLE_COLORS[identity.role] || 'gray'}>
                  {identity.role}
                </Badge>
              </Button>
            ))}
          </Stack>
        )}

        <Text fontSize="xs" color="redesign.text.muted" mt={6} textAlign="center">
          All of these share one workspace, so the data is identical — only the
          permissions differ.
        </Text>
      </Box>
    </Flex>
  );
};

export default CleanroomLogin;
